import React from 'react';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import {
	useMarketStateDayPriceEvolutionForHistoryQuery,
} from '@app/model/market_state/service/MarketStateSnapshotServiceHooks';
import Loader from '@app/component/loader/Loader';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketSateDayPriceEvolutionMedianGraph
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/timeline/MarketSateDayPriceEvolutionMedianGraph';
import MarketSateDayPriceEvolutionAnalyticalGraph
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/timeline/MarketSateDayPriceEvolutionAnalyticalGraph';
import Message from '@app/component/message/Message';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import useSecurity from '@util/SecurityHook';
import ACLAuthority from '@app/model/profile/ACLAuthority';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import type { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';

// One empty list for all renders: a new [] would make the graphs rebuild their chart options.
const NO_PRICE_CHANGES: PriceChangeSubmission[] = [];


const MarketSateDayPriceEvolutionGraphs: React.FunctionComponent<{
	calendarDay: CalendarDay,
	tab: 'ANALYTICAL' | 'MEDIAN'
}> = ({ calendarDay, tab }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { filter } = useMarketStateFilterInputTools();
	const { hasPricingAuthority } = useSecurity();
	const fromDate = moment().subtract(45, 'days').format('YYYY-MM-DD');
	const { data: priceChanges } = useQuery({
		queryKey: PricingAutomationQueryKeys.priceChangesForStatDate(companyHotel.id, calendarDay.display, fromDate),
		queryFn: () => PricingAutomationService.findCompanyHotelPriceChangesForStatDate(companyHotel.id, calendarDay.display, fromDate),
		enabled: hasPricingAuthority(ACLAuthority.READ),
		refetchOnWindowFocus: false,
	});


	const { data, isLoading, isSuccess } = useMarketStateDayPriceEvolutionForHistoryQuery(
		companyHotel.id,
		calendarDay.display,
		filter);

	if (isLoading) return <Loader />;

	if (isSuccess) {

		if (tab === 'ANALYTICAL') {
			return <MarketSateDayPriceEvolutionAnalyticalGraph data={data!} priceChanges={priceChanges ?? NO_PRICE_CHANGES} />;
		} else if (tab === 'MEDIAN') {
			return <MarketSateDayPriceEvolutionMedianGraph data={data!} priceChanges={priceChanges ?? NO_PRICE_CHANGES} />;
		}
	}
	return <Message state="warning">The graph is not currently available</Message>;
};

export default MarketSateDayPriceEvolutionGraphs;
